import { FiltersType, ReferenceLocation, UnitsMultilevelList } from '@/types';
import { formatSearchResultLocationPhrase, getFormatSelectedUnits } from '@/common';

type AdmUnit = { id: string; name: string; parent_name: string };

type BuildSearchTitleTranslations = {
  isSell: string;
  isRent: string;
  in: string;
  item?: string;
  items?: string;
  projectItem?: string;
  projectItems?: string;
  andMore?: (count: number) => string;
};

type BuildLocationLabelOptions = {
  admunits?: AdmUnit[];
  locations?: ReferenceLocation[];
  unitListValue?: string[];
  unitList?: UnitsMultilevelList;
  /** Passed as t() function — only used for getFormatSelectedUnits */
  t?: (key: string) => string;
  /** Client-only: formats overflow label e.g. "und 3 weitere" */
  andMore?: (count: number) => string;
};

const COLLAPSE_THRESHOLD = 3;
const MAX_SHOWN = 2;

const isNumeric = (s: string) => /^\d+$/.test(s.trim());

/**
 * Builds admunit label from enriched AdmUnit objects (those with name/parent_name).
 * Returns '' if no names available (backend not yet enriched).
 */
export const buildAdmunitsLabel = (
  admunits: AdmUnit[],
  andMore?: (count: number) => string,
): string => {
  const groups = new Map<string, string[]>();
  for (const a of admunits) {
    const names = groups.get(a.parent_name) ?? [];
    names.push(a.name);
    groups.set(a.parent_name, names);
  }

  const labels: string[] = [];
  for (const [parent, names] of groups.entries()) {
    if (names.length >= COLLAPSE_THRESHOLD) {
      if (parent && !isNumeric(parent)) labels.push(`${parent} (${names.length})`);
      else
        for (const name of names) {
          if (name && !isNumeric(name)) labels.push(name);
        }
    } else {
      for (const name of names) {
        if (!isNumeric(name)) {
          labels.push(name);
        } else if (parent && !isNumeric(parent)) {
          labels.push(parent);
        }
      }
    }
  }

  if (!labels.length) return '';
  if (labels.length <= MAX_SHOWN) return labels.join(', ');
  const overflow = labels.length - MAX_SHOWN;
  const suffix = andMore ? ` ${andMore(overflow)}` : '';
  return `${labels.slice(0, MAX_SHOWN).join(', ')}${suffix}`;
};

/**
 * Shared location label builder used by both server (buildDirectLinkTitle)
 * and client (Head.tsx).
 *
 * Priority:
 * 1. admunits with names (map widget selection — enriched objects)
 * 2. locations[] (ReferenceLocation from autocomplete)
 * 3. unitListValue resolved via unitList (country/region/district codes)
 */
export const buildLocationLabel = ({
  admunits,
  locations,
  unitListValue,
  unitList,
  t,
  andMore,
}: BuildLocationLabelOptions): string => {
  if (admunits?.length) {
    const hasNames = admunits.some((a) => !!a.name);
    if (hasNames) return buildAdmunitsLabel(admunits, andMore);
  }

  if (locations?.length) {
    const names = locations.map((l) => l.name).filter(Boolean);
    if (!names.length) return '';
    if (names.length <= MAX_SHOWN) return names.join(', ');
    const overflow = names.length - MAX_SHOWN;
    const suffix = andMore ? ` ${andMore(overflow)}` : '';
    return `${names.slice(0, MAX_SHOWN).join(', ')}${suffix}`;
  }

  if (unitListValue?.length && unitList) {
    return getFormatSelectedUnits(unitListValue, unitList, t ?? (() => ''), andMore);
  }

  return '';
};

type BuildSearchTitleOptions = {
  offerType: FiltersType['offerTypeValue'];
  /** Category display names (already resolved, e.g. ["Wohnungen", "Häuser"]) */
  categoryTitles: string[];
  locationLabel: string;
  translations: BuildSearchTitleTranslations;
  locale?: string;
  /**
   * When provided, prefix title with "{numFound} {item/items}".
   * Pass undefined for directlink SSR (no count, uses categoryTitles instead).
   * Pass null to show item label without count (e.g. MAP tab before selection).
   */
  numFound?: number | null;
  isProject?: boolean;
};

/**
 * Builds the search result h1/title string.
 *
 * Used in:
 * - Head.tsx (client, with numFound)
 * - buildDirectLinkTitle (server, without numFound)
 *
 * Format without count:  "{categories} {isSell/isRent} {locationPhrase}"
 * Format with count:     "{numFound} {item(s)} {isSell/isRent} {locationPhrase}"
 * Project format:        "{numFound} {projectItem(s)} {locationPhrase}"
 */
export const buildSearchTitle = ({
  offerType,
  categoryTitles,
  locationLabel,
  translations,
  locale = 'de',
  numFound,
  isProject = false,
}: BuildSearchTitleOptions): string => {
  const offerTypePart = offerType === 'SELL' ? translations.isSell : translations.isRent;

  const locationPhrase = locationLabel
    ? ` ${formatSearchResultLocationPhrase(locationLabel, translations.in, locale)}`
    : '';

  if (isProject) {
    const itemLabel =
      numFound !== 1 ? (translations.projectItems ?? '') : (translations.projectItem ?? '');
    const countPrefix = numFound != null ? `${numFound} ${itemLabel} ` : '';
    return `${countPrefix}${locationPhrase}`.trim();
  }

  if (numFound != null) {
    // numFound is a number (including 0) — show count + item label
    const itemLabel = numFound !== 1 ? (translations.items ?? '') : (translations.item ?? '');
    return `${numFound} ${itemLabel} ${offerTypePart}${locationPhrase}`.trim();
  }

  if (numFound === null) {
    // null = show item label without count (MAP tab before selection)
    const itemLabel = translations.items ?? '';
    return `${itemLabel} ${offerTypePart}${locationPhrase}`.trim();
  }

  // undefined — directlink SSR format: categories + offerType + location (no count)
  const categoryPart = categoryTitles.length ? `${categoryTitles.join(', ')} ` : '';
  return `${categoryPart}${offerTypePart}${locationPhrase}`.trim();
};
